import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, FileCheck, Gem, Handshake, Truck, UserCheck } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { CONTACT, IMG } from "@/data/site";

const STATS = [
  { value: "50+", label: "Years — Family Legacy" },
  { value: "10+", label: "Years — Arun's Specialisation" },
  { value: "12+", label: "Leading Brands" },
  { value: "1", label: "Address in Faridabad" },
];

const VALUES = [
  { icon: Gem, title: "Curation over Catalogue", desc: "We don't stock everything — we stock what lasts. Every fitting and tile on our floor has earned its place." },
  { icon: Compass, title: "Right Product, Right Budget", desc: "Premium or value, fittings or tiles — we show honest options across Jaquar, Kohler, Hindware, Kajaria and more." },
  { icon: Handshake, title: "Family Word, Brand Warranty", desc: "A family-run house built on five decades of repeat customers, backed by official brand warranties and genuine billing." },
];

const TRADE = [
  { icon: FileCheck, title: "BOQ & Project Pricing", desc: "Share your bill of quantities — we return brand-wise, line-item quotations within 48 hours." },
  { icon: Truck, title: "Scheduled Site Delivery", desc: "Phase-wise deliveries timed to your contractor's schedule, across Faridabad and NCR sites." },
  { icon: UserCheck, title: "Dedicated Account Support", desc: "One point of contact for stock checks, samples, site issues and warranty documentation." },
];

export default function About() {
  usePageMeta(
    "About Us | 50-Year Family Legacy — SRK Bath and Tiles, Faridabad",
    "Shri Radhey Krishna Bath and Tiles brings a 50-year family building-materials legacy to Greater Faridabad. Led by Arun Tayal — Jaquar, Kohler, Hindware & designer tiles for homeowners, architects and developers."
  );
  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden pb-16 pt-36 sm:pb-24 sm:pt-48">
        <div className="pointer-events-none absolute -left-40 top-24 h-96 w-96 rounded-full bg-brand/5 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-brand"
          >
            Our Story · Greater Faridabad
          </motion.p>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl" data-testid="about-headline">
            {["A 50-year legacy,", "brought home to Faridabad."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.215, 0.61, 0.355, 1] }}
                >
                  {i === 1 ? <em className="text-brand-gradient not-italic">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16">
          <Reveal className="lg:col-span-6">
            <div className="img-spotlight relative overflow-hidden border border-ink/10">
              <img src={IMG.heroShowroom} alt="Shri Radhey Krishna Bath and Tiles showroom, Greater Faridabad" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <Eyebrow>Family Run Since Generations</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl">
              Five decades of trading trust — <em className="text-brand-gradient not-italic">now on Tigaon Road.</em>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5C6570]">
              Our family has been in the building-materials trade for over 50 years —
              through wholesale yards, old-city markets and three generations of builders
              who still call us first. Shri Radhey Krishna Bath and Tiles is that legacy's
              Faridabad chapter: the same sourcing depth and fair dealing, in a modern
              showroom format the new sectors deserve.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#5C6570]">
              It is led by {CONTACT.owner}, who has spent over a decade specialising in
              bath fittings and designer tiles — advising homeowners on their first
              renovation and architects on their hundredth project alike.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#5C6570]">
              We deliver daily across Sectors 70 to 89, Neharpar, Ballabhgarh and the
              wider NCR — and high-end architects and developers from Gurgaon to South
              Delhi now route their Faridabad projects through our trade desk first.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-stats-section" className="border-y border-brand/30 bg-ink py-14 sm:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-4 sm:px-8 lg:grid-cols-4 lg:px-16">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="text-center">
              <p className="font-serif text-4xl font-medium text-[#E8694F] sm:text-5xl" data-testid={`stat-value-${i}`}>
                {s.value}
              </p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#9AA6B2]">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="trade-section" className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="mb-14">
            <Eyebrow>For Architects &amp; Developers</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl lg:text-4xl">
              A trade desk built for <em className="text-brand-gradient not-italic">your projects.</em>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5C6570]">
              Premium residences, boutique hotels, builder floors — we support high-end
              projects across Faridabad and NCR with the discipline our family has
              practised for fifty years.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {TRADE.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.12}>
                <div
                  data-testid={`trade-card-${i}`}
                  className="group h-full border border-ink/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand/50 sm:p-10"
                >
                  <t.icon size={28} className="text-brand" />
                  <h3 className="mt-6 font-serif text-xl font-semibold text-foreground sm:text-2xl">{t.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#5C6570]">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link
              to="/contact"
              data-testid="trade-partner-button"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-brand-bright"
            >
              Partner With Us <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="mb-14">
            <Eyebrow>What We Stand By</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl lg:text-4xl">
              Three promises, <em className="text-brand-gradient not-italic">kept for generations.</em>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.12}>
                <div
                  data-testid={`value-card-${i}`}
                  className="group h-full border border-ink/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand/50 sm:p-10"
                >
                  <v.icon size={28} className="text-brand" />
                  <h3 className="mt-6 font-serif text-xl font-semibold text-foreground sm:text-2xl">{v.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#5C6570]">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="img-spotlight relative overflow-hidden border border-brand/25 bg-white p-10 sm:p-16">
              <span className="absolute left-0 top-0 h-px w-full shimmer-line" />
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <Eyebrow>Visit Us</Eyebrow>
                  <h2 className="mt-4 font-serif text-2xl font-medium leading-tight text-foreground sm:text-3xl">
                    Tigaon Road, Sector 86 — walk in any day, 10 AM to 8 PM.
                  </h2>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link
                    to="/contact"
                    data-testid="about-visit-button"
                    className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-brand-bright"
                  >
                    Plan Your Visit <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
